import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';

import { Subscription, debounceTime, fromEvent, merge } from 'rxjs';

import { AnalyticsEvent, Ga4TrackingEventMap, Ga4TrackingEventName, NavigationItems } from '@hdms/shared/common-types';

import { WindowRefService } from '../window-ref/window-ref.service';

import { AnalyticsService } from './analytics.service';
import { Ga4TrackingEventDto } from './ga4-tracking-event-dto';

enum EventType {
  DEFAULT = 'default',
  PAGE_VIEW = 'pageView'
}

@Directive({
  selector: '[shrdUtcTrackEvent], [shrdUtcTrackGa4Event]'
})
export class TrackEventDirective implements OnInit, OnDestroy {
  public static readonly debounceTime = 100;

  @Input() shrdUtcTrackEvent: AnalyticsEvent;
  @Input() shrdUtcTrackGa4Event?: Ga4TrackingEventMap[Ga4TrackingEventName];
  @Input() eventType?: 'default' | 'pageView' | Ga4TrackingEventName = EventType.DEFAULT;
  @Input() omitInitialTrackingData = true;

  /**
   * For circumventing possibly applied stopPropagation / returns
   * inside additional click handlers on the element (which could
   * block tracking event from being dispatched),
   * mouseup event is used, as it dispatches before click.
   *
   * On mobile devices it's not required to listen for touchend as
   * there the mouseup event also will be triggered as usual.
   *
   * For accessibility reasons listen to space and enter as well.
   */
  private listenToEvents: string[] = ['mouseup', 'keydown.space', 'keydown.enter'];
  @Input() set listenTo(events: string | string[]) {
    this.listenToEvents = Array.isArray(events) ? events : [events];
  }

  private subscription: Subscription;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly winRef: WindowRefService,
    private readonly elRef: ElementRef
  ) {}

  public onUserInput(): void {
    if (this.eventType === EventType.DEFAULT) {
      this.analyticsService.sendEvent(this.shrdUtcTrackEvent, this.omitInitialTrackingData);
    } else if (this.eventType === EventType.PAGE_VIEW) {
      const location = this.winRef.nativeWindow.location;
      // track previous page path and template type for tabs switching as well
      if (this.shrdUtcTrackEvent.navigationItem === NavigationItems.TABS) {
        // as tabs will be on the same url path and needs tracking, passed allowEqualUrl  as true
        this.analyticsService.trackPageViewWithPrevPageData(location.pathname, this.shrdUtcTrackEvent, true);
      } else {
        this.analyticsService.trackPageView(location.href.substring(location.origin.length), this.shrdUtcTrackEvent);
      }
    } else if (this.shrdUtcTrackGa4Event && this.eventType) {
      const eventData = Ga4TrackingEventDto.create(this.eventType, this.shrdUtcTrackGa4Event);
      this.analyticsService.sendEvent(eventData, this.omitInitialTrackingData);
    }
  }

  ngOnInit(): void {
    const events$ = merge(...this.listenToEvents.map(eventName => fromEvent(this.elRef.nativeElement, eventName)));

    // Delay tracking for `pageView` events only, to ensure that new location will be properly picked up.
    // In other cases don't delay, as this could lead to dropped events, e.g. while requesting a new url.
    this.subscription = (
      this.eventType === EventType.PAGE_VIEW ? events$.pipe(debounceTime(TrackEventDirective.debounceTime)) : events$
    ).subscribe(this.onUserInput.bind(this));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
